import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useDirtyGuard } from '@/hooks/useDirtyGuard';
import { useAppSelector } from '@/features/hooks';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { DraftRestoredBanner } from '@/components/common/DraftRestoredBanner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { FieldTooltip } from '@/components/ui/field-tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { ColorPicker } from '@/components/ui/color-picker';
import { CurrencySelect } from '@/components/ui/currency-select';
import { useCurrency } from '@/hooks/useCurrency';
import type { Account } from '@/lib/types';

const accountSchema = z.object({
  name: z.string().min(1, 'Account name is required').max(100),
  accountType: z.enum([
    'checking',
    'savings',
    'credit_card',
    'investment',
    'loan',
    'mortgage',
    'manual',
    'crypto',
    'other',
  ]),
  balance: z.coerce.number({ invalid_type_error: 'Balance must be a number' }),
  currency: z.string().length(3, 'Use a 3-letter currency code'),
  institutionName: z.string().max(100).optional().or(z.literal('')),
  accountNumber: z.string().max(50).optional().or(z.literal('')),
  creditLimit: z.coerce.number().optional().or(z.literal('')).transform((v) => (v === '' ? undefined : v)),
  interestRate: z.coerce.number().min(0).max(100).optional().or(z.literal('')).transform((v) => (v === '' ? undefined : v)),
  isManual: z.boolean().default(false),
  isActive: z.boolean().default(true),
  notes: z.string().max(500).optional().or(z.literal('')),
  emoji: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/i, 'Use a hex color like #3b82f6')
    .default('#3b82f6'),
});

export type AccountFormValues = z.infer<typeof accountSchema>;

interface AccountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AccountFormValues) => Promise<void>;
  account?: Account | null;
  isSubmitting?: boolean;
}

const accountTypeLabels: Record<AccountFormValues['accountType'], string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit_card: 'Credit Card',
  investment: 'Investment',
  loan: 'Loan',
  mortgage: 'Mortgage',
  manual: 'Manual',
  crypto: 'Crypto',
  other: 'Other',
};

export function AccountForm({ open, onOpenChange, onSubmit, account, isSubmitting }: AccountFormProps) {
  const isEditing = !!account;
  const { currency: userCurrency } = useCurrency();
  const userId = useAppSelector((state) => state.auth.user?.id);

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: '',
      accountType: 'checking',
      balance: 0,
      currency: userCurrency,
      institutionName: '',
      accountNumber: '',
      creditLimit: undefined,
      interestRate: undefined,
      isManual: false,
      isActive: true,
      notes: '',
      emoji: '',
      color: '#3b82f6',
    },
  });

  const { clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore } = useFormDraft<AccountFormValues>(
    'account-create',
    userId,
    form,
    !isEditing && open
  );

  const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard, saveDraftAndClose } =
    useDirtyGuard(form, onOpenChange, clearDraft, saveDraftNow);

  useEffect(() => {
    if (account) {
      form.reset({
        name: account.name,
        accountType: account.accountType,
        balance: account.balance,
        currency: account.currency || 'USD',
        institutionName: account.institutionName || account.institution || '',
        accountNumber: account.accountNumber || '',
        creditLimit: account.creditLimit,
        interestRate: account.interestRate || undefined,
        isManual: !!account.isManual,
        isActive: account.isActive,
        notes: account.notes || '',
        emoji: account.emoji || '',
        color: account.color || '#3b82f6',
      });
    } else {
      form.reset({
        name: '',
        accountType: 'checking',
        balance: 0,
        currency: userCurrency,
        institutionName: '',
        accountNumber: '',
        creditLimit: undefined,
        interestRate: undefined,
        isManual: false,
        isActive: true,
        notes: '',
        emoji: '',
        color: '#3b82f6',
      });
    }
  }, [account, form, open, userCurrency]);

  const handleSubmit = async (values: AccountFormValues) => {
    await onSubmit(values);
    clearDraft();
    form.reset();
  };

  return (
    <>
    <Sheet open={open} onOpenChange={guardedOnOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Account' : 'Add Account'}</SheetTitle>
          <SheetDescription>
            {isEditing ? 'Update the account details below.' : 'Create a new financial account.'}
          </SheetDescription>
        </SheetHeader>

        {justRestored && (
          <DraftRestoredBanner
            age={restoredAge}
            onDismiss={dismissRestore}
            className="mx-0 mt-4"
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account name<RequiredAsterisk /><FieldTooltip text="A recognizable name for this account, e.g., 'Chase Checking', 'Savings at Ally'." /></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Chase Checking" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emoji<OptionalTag /><FieldTooltip text="A visual tag for quick scanning. Purely decorative." /></FormLabel>
                  <FormControl>
                    <EmojiPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color<OptionalTag /><FieldTooltip text="Assign a color to differentiate this account in charts." /></FormLabel>
                  <FormControl>
                    <ColorPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accountType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type<RequiredAsterisk /><FieldTooltip text="The type of financial account: checking, savings, credit card, investment, etc." /></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(accountTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="balance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Balance<RequiredAsterisk /><FieldTooltip text="Current account balance. For credit cards, enter the amount you owe as a positive number." /></FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency<RequiredAsterisk /><FieldTooltip text="The currency used by this account. Transactions default to the account's currency." /></FormLabel>
                    <FormControl>
                      <CurrencySelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        required
                        testId="currency-select-account"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="institutionName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Institution<OptionalTag /><FieldTooltip text="Bank or financial provider name for your reference." /></FormLabel>
                    <FormControl>
                      <Input placeholder="Bank or provider" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account number (last 4)<OptionalTag /><FieldTooltip text="Last 4 digits only, for identification. Never store full account numbers." /></FormLabel>
                    <FormControl>
                      <Input placeholder="1234" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="creditLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit limit<OptionalTag /><FieldTooltip text="For credit cards, enter your credit limit. Used to calculate utilization." /></FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="interestRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interest rate %<OptionalTag /><FieldTooltip text="Annual interest rate for this account. Useful for debt payoff planning." /></FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes<OptionalTag /><FieldTooltip text="Internal notes — context, limits, or reminders about this account." /></FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add context, limits, or reminders" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="isManual"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0 rounded-md border p-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm">Manual account</FormLabel>
                      <p className="text-xs text-muted-foreground">Track balances manually (no sync).</p>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0 rounded-md border p-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm">Active</FormLabel>
                      <p className="text-xs text-muted-foreground">Inactive accounts are hidden from totals.</p>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter>
              <Button variant="outline" type="button" onClick={() => guardedOnOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save changes' : 'Create account'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
      <DiscardChangesDialog
        open={showDiscard}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        onSaveDraft={!isEditing ? saveDraftAndClose : undefined}
      />
    </>
  );
}
